/**
 * Prepayment Pool Service
 *
 * Handles API calls for prepayment pool management including:
 * - Getting pool summaries and balances
 * - Creating contributions and consuming prepayments
 * - Getting transaction history and statistics
 */

import { apiClient } from '../utils/api-client';

import type {
  PrepaymentPoolSummary,
  PrepaymentTransaction,
  PrepaymentContributionRequest,
  PrepaymentConsumptionRequest,
  InvestorPoolItem,
  PageResult,
} from '../types/drawdown';

// Helper function to build query string
function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

class PrepaymentPoolService {
  /**
   * Get comprehensive prepayment pool summary for a fund
   */
  static async getPoolSummary(fundId: string): Promise<PrepaymentPoolSummary> {
    return apiClient.get(`/funds/${fundId}/drawdowns/prepayment-pool`);
  }

  /**
   * Get paginated investor pools for a fund
   */
  static async getInvestorPoolsPaginated(
    fundId: string,
    page: number = 1,
    pageSize: number = 10,
    onlyWithBalance: boolean = true,
    search?: string
  ): Promise<PageResult<InvestorPoolItem>> {
    const queryString = buildQueryString({
      page,
      page_size: pageSize,
      only_with_balance: onlyWithBalance,
      search,
    });
    return apiClient.get(
      `/funds/${fundId}/drawdowns/prepayment-pool/investors?${queryString}`
    );
  }

  /**
   * Get available prepayment balances for all investors in a fund
   */
  static async getPoolBalances(fundId: string): Promise<{
    fund_id: string;
    balances: Array<{ investor_id: string; available_balance: number }>;
  }> {
    return apiClient.get(`/funds/${fundId}/drawdowns/prepayment-pool/balances`);
  }

  /**
   * Get prepayment transaction history for a specific investor
   */
  static async getInvestorHistory(
    fundId: string,
    investorId: string,
    limit: number = 50
  ): Promise<{
    investor_id: string;
    fund_id: string;
    current_balance: number;
    total_contributed: number;
    total_consumed: number;
    currency: string;
    transactions: Array<{
      id: string;
      transaction_type: string;
      amount: number;
      balance_after: number;
      currency: string;
      description?: string;
      created_at: string;
      drawdown_id: string;
    }>;
  }> {
    const queryString = buildQueryString({ limit });
    return apiClient.get(
      `/funds/${fundId}/drawdowns/prepayment-pool/investors/${investorId}/history?${queryString}`
    );
  }

  /**
   * Create a prepayment contribution (from regulatory capital call payment)
   */
  static async createContribution(
    fundId: string,
    drawdownId: string,
    contributionData: PrepaymentContributionRequest
  ): Promise<PrepaymentTransaction> {
    return apiClient.post(
      `/funds/${fundId}/drawdowns/${drawdownId}/prepayment-pool/contribute`,
      contributionData
    );
  }

  /**
   * Consume prepayment pool for a capital call
   */
  static async consumePrepayment(
    fundId: string,
    drawdownId: string,
    consumptionData: PrepaymentConsumptionRequest
  ): Promise<PrepaymentTransaction> {
    return apiClient.post(
      `/funds/${fundId}/drawdowns/${drawdownId}/prepayment-pool/consume`,
      consumptionData
    );
  }

  /**
   * Get prepayment pool statistics for reporting
   */
  static async getPoolStatistics(
    fundId: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<{
    fund_id: string;
    period: {
      date_from?: string;
      date_to?: string;
    };
    contributions: {
      count: number;
      total_amount: number;
    };
    consumptions: {
      count: number;
      total_amount: number;
    };
    current_status: {
      investor_pool_count: number;
      total_available_balance: number;
      total_lifetime_contributed: number;
      total_lifetime_consumed: number;
    };
  }> {
    const params: Record<string, unknown> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    const queryString = buildQueryString(params);
    return apiClient.get(
      `/funds/${fundId}/drawdowns/prepayment-pool/statistics?${queryString}`
    );
  }
}

export const prepaymentPoolService = PrepaymentPoolService;
